import { ClearOutlined } from "@mui/icons-material";
import {
  IconButton,
  MenuItem,
  Select,
  SxProps,
  Theme,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions
} from "react-hook-form";
import { getOptions } from "../../../api/asyncTypeaheadClient";
import { SelectOption } from "../../../models/form";
import DOMPurify from "dompurify";

interface HeiAsyncDropdownProps<TForm extends FieldValues> {
  control: Control<TForm>;
  name: Path<TForm>;
  label: string;
  placeholder?: string;
  sx?: SxProps<Theme>;
  rules?: Omit<
    RegisterOptions<TForm>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  endpoint: string;
  title?: string;
  description?: string;
  disabled?: boolean;
  preselectedValue?: string;
}

const HeiAsyncDropdown = <TForm extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  sx,
  rules,
  endpoint,
  title,
  description,
  disabled,
  preselectedValue = ""
}: HeiAsyncDropdownProps<TForm>): JSX.Element => {
  const required = rules?.required;

  const [options, setOptions] = useState([] as SelectOption[]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const callEndpoint = async () => {
      setIsLoading(true);
      setOptions(await getOptions(endpoint, ""));
      setIsLoading(false);
    };

    callEndpoint();
  }, [endpoint]);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ fieldState, field: { ref, onChange, ...field } }) => {
        const error = fieldState.error;
        const errorMessage = error?.message;
        if (!field.value && preselectedValue && !isInitialized && options.length > 0) {
          setIsInitialized(true);
          onChange(preselectedValue);
          field.onBlur();
        }
        return (
          <>
            {title && (
              <Typography
                variant="body2"
                sx={{ color: "#202020", mb: 1, mt: 1 }}
              >
                {title}
              </Typography>
            )}
            {description && (
              <Typography variant="body2" sx={{ mb: 3 }}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(description),
                  }}
                />
              </Typography>
            )}
            <Select
              ref={ref}
              required={!!required}
              disabled={disabled}
              sx={{ ...sx, width: "70%" }}
              error={error !== undefined}
              value={field.value ?? ""}
              placeholder={placeholder}
              label={label}
              onChange={(e) => {
                onChange(e.target.value);
                field.onBlur();
              }}
              onBlur={() => { field.onBlur(); }}
              IconComponent={field.value ? () => (
                <IconButton onClick={() => { onChange(""); field.onBlur(); }} size="small">
                  <ClearOutlined />
                </IconButton>
              ) : undefined
              }
            >
              <MenuItem key="NULLVALUE" value="" disabled>Select...</MenuItem>
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {errorMessage && (
              <Typography variant="body2" sx={{ color: "error.main", mt: 1 }}>
                {errorMessage}
              </Typography>
            )}
          </>
        );
      }}
    />
  );
};

export default HeiAsyncDropdown;
